// PORTED FROM apps/web — see apps/web-employer/PORTING.md (or web-admin/) for follow-ups

import * as React from "react";
import { useTranslations } from "next-intl";
import {
  ADVANTAGE_LABEL_VI,
  AVAILABLE_FROM_LABEL_VI,
  EDUCATION_LEVEL_LABEL_VI,
  EMPLOYMENT_TYPE_LABEL_VI,
  GENDER_LABEL_VI,
  LIVING_ARRANGEMENT_LABEL_VI,
  PROVINCE_NAME_VI,
  SALARY_TYPE_LABEL_VI,
  SKILL_LABEL_VI,
  WORKER_POSITION_LABEL_VI,
  type Advantage,
  type AvailableFrom,
  type EducationLevel,
  type EmploymentType,
  type Gender,
  type LivingArrangement,
  type ProvinceCode,
  type SalaryType,
  type WorkerPosition,
} from "@chotca/config";
import {
  Avatar,
  Badge,
  Button,
  Dialog,
  formatDateVn,
  formatVnd,
  Spinner,
} from "@chotca/ui";
import { UserPlus, X as XIcon } from "lucide-react";
import { useApplicantProfile } from "@chotca/api-client";
import { useAdminApplicantProfile } from "@chotca/api-client";

type Props = {
  applicationId: string | null;
  open: boolean;
  onClose: () => void;
  onInvite: () => void;
  onReject: () => void;
  invitePending: boolean;
  rejectPending: boolean;
  mode?: "employer" | "admin";
  inviteLabel?: string;
};

export function WorkerProfileModal({
  applicationId,
  open,
  onClose,
  onInvite,
  onReject,
  invitePending,
  rejectPending,
  mode = "employer",
  inviteLabel,
}: Props) {
  const t = useTranslations("applicantProfile");
  const employerProfile = useApplicantProfile(
    mode === "employer" && open ? applicationId : null,
  );
  const adminProfile = useAdminApplicantProfile(
    mode === "admin" && open ? applicationId : null,
  );
  const profile = mode === "admin" ? adminProfile : employerProfile;

  const data = profile.data;
  // `invited` = an invite was sent (drives action buttons). `committed` = the
  // invite committed, which is the only point contact + full name unlock.
  const invited = data?.invited ?? false;
  const committed = !!data?.contact;
  const rejected = data?.application.state === "REJECTED";
  const withdrawn = data?.application.state === "WITHDRAWN";
  const canAct = !invited && !rejected && !withdrawn;

  const displayName =
    committed && data?.worker.fullName
      ? data.worker.fullName
      : (data?.worker.maskedName ?? "—");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t("title")}
      size="lg"
      footer={
        data && canAct ? (
          <>
            <Button
              variant="danger"
              size="sm"
              onClick={onReject}
              loading={rejectPending}
              disabled={invitePending}
            >
              <XIcon className="h-4 w-4" />
              {t("actionReject")}
            </Button>
            <Button
              size="sm"
              onClick={onInvite}
              loading={invitePending}
              disabled={rejectPending}
            >
              <UserPlus className="h-4 w-4" />
              {inviteLabel ?? t("actionViewContact")}
            </Button>
          </>
        ) : null
      }
    >
      {profile.isLoading ? (
        <div className="flex items-center justify-center gap-2 py-12 text-ash">
          <Spinner /> <span>{t("loading")}</span>
        </div>
      ) : profile.isError || !data ? (
        <p className="py-12 text-center text-sm text-danger">
          {t("loadError")}
        </p>
      ) : (
        <ProfileBody
          data={data}
          displayName={displayName}
          committed={committed}
          t={
            t as unknown as (
              key: string,
              values?: Record<string, string | number>,
            ) => string
          }
        />
      )}
    </Dialog>
  );
}

type ProfileData = NonNullable<ReturnType<typeof useApplicantProfile>["data"]>;

function ProfileBody({
  data,
  displayName,
  committed,
  t,
}: {
  data: ProfileData;
  displayName: string;
  committed: boolean;
  t: (key: string, values?: Record<string, string | number>) => string;
}) {
  const w = data.worker;

  return (
    <div className="max-h-[70vh] space-y-6 overflow-y-auto pr-1">
      <header className="flex items-start gap-4">
        <Avatar
          name={displayName}
          src={w.avatarUrl ?? undefined}
          size="xl"
          tone="lime"
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-lg font-extrabold text-ink">{displayName}</h3>
            {w.kycVerifiedAt ? (
              <Badge tone="lime-soft">✓ {t("kycVerified")}</Badge>
            ) : null}
          </div>
          <p className="mt-1 text-sm text-ash">
            {[
              w.age != null ? t("ageYears", { age: w.age }) : null,
              w.gender ? GENDER_LABEL_VI[w.gender as Gender] : null,
              w.currentDistrict,
            ]
              .filter(Boolean)
              .join(" · ")}
          </p>
          <p className="mt-1 text-xs text-ash">
            {t("memberSince", { date: formatDateVn(w.memberSince) })}
          </p>
          <div className="mt-2 flex flex-wrap gap-3 text-xs text-ash">
            <span>
              {w.ratingAvg !== null
                ? `⭐ ${w.ratingAvg.toFixed(1)} · ${t("ratingsCount", { count: w.ratingCount })}`
                : t("noRatings")}
            </span>
            <span>
              {t("stats", {
                applied: w.totalApplications,
                invited: w.totalInvites,
              })}
            </span>
            <span>{t("completion", { pct: w.completionPct })}</span>
          </div>
          <div className="mt-2">
            <Badge tone={committed ? "lime" : "sun"}>
              {committed ? t("contactUnlocked") : t("contactHidden")}
            </Badge>
          </div>
        </div>
      </header>

      {committed && data.contact ? (
        <section className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">
            {t("contactUnlocked")}
          </p>
          <p className="mt-1 font-mono text-base font-bold text-ink">
            {data.contact.phone ?? "Chưa có SĐT"}
          </p>
          {data.contact.email ? (
            <p className="text-sm text-ink/70">{data.contact.email}</p>
          ) : null}
          {data.contact.phone ? (
            <a
              href={`https://zalo.me/${data.contact.phone.replace(/\D/g, "")}`}
              target="_blank"
              rel="noreferrer"
              className="mt-2 inline-block rounded-full bg-emerald-600 px-3 py-1 text-[11px] font-bold text-white"
            >
              Mở Zalo
            </a>
          ) : null}
        </section>
      ) : null}

      <ExperienceSection w={w} t={t} />

      <Section title={t("sectionAbout")}>
        <p className="whitespace-pre-line text-sm text-ink/80">
          {w.bioShort?.trim() || t("noBio")}
        </p>
      </Section>

      <Section title={t("sectionWantedJob")}>
        <FieldList>
          <Field label={t("labelDesiredPositions")}>
            <ChipList
              items={w.desiredPositions.map(
                (p) => WORKER_POSITION_LABEL_VI[p as WorkerPosition] ?? p,
              )}
            />
          </Field>
          <Field label={t("labelEmploymentTypes")}>
            <ChipList
              items={w.employmentTypes.map(
                (e) => EMPLOYMENT_TYPE_LABEL_VI[e as EmploymentType] ?? e,
              )}
            />
          </Field>
          <Field label={t("labelDesiredAreas")}>
            <ChipList items={w.desiredAreas} />
          </Field>
          <Field label={t("labelDesiredSalary")}>
            <span className="text-sm text-ink/80">
              {formatSalary(
                w.desiredSalaryMin,
                w.desiredSalaryMax,
                w.desiredSalaryType,
              )}
            </span>
          </Field>
          <Field label={t("labelAvailableFrom")}>
            <span className="text-sm text-ink/80">
              {w.availableFrom
                ? AVAILABLE_FROM_LABEL_VI[w.availableFrom as AvailableFrom]
                : "—"}
            </span>
          </Field>
        </FieldList>
      </Section>

      <Section title={t("sectionSkills")}>
        <FieldList>
          <Field label={t("labelSkills")}>
            <ChipList items={w.skills.map((s) => SKILL_LABEL_VI[s] ?? s)} />
          </Field>
          <Field label={t("labelAdvantages")}>
            <ChipList
              items={w.advantages.map(
                (a) => ADVANTAGE_LABEL_VI[a as Advantage] ?? a,
              )}
            />
          </Field>
          {w.languages && w.languages.length > 0 ? (
            <Field label={t("labelLanguages")}>
              <ChipList
                items={w.languages.map((l) => `${l.lang} (${l.level})`)}
              />
            </Field>
          ) : null}
        </FieldList>
      </Section>

      <Section title={t("sectionPersonal")}>
        <FieldList>
          <Field label={t("labelHeight")}>
            <span className="text-sm text-ink/80">
              {w.heightCm ? `${w.heightCm} cm` : "—"}
            </span>
          </Field>
          <Field label={t("labelWeight")}>
            <span className="text-sm text-ink/80">
              {w.weightKg ? `${w.weightKg} kg` : "—"}
            </span>
          </Field>
          <Field label={t("labelEducation")}>
            <span className="text-sm text-ink/80">
              {w.education
                ? EDUCATION_LEVEL_LABEL_VI[w.education as EducationLevel]
                : "—"}
            </span>
          </Field>
          <Field label={t("labelHometown")}>
            <span className="text-sm text-ink/80">
              {w.hometown ? PROVINCE_NAME_VI[w.hometown as ProvinceCode] : "—"}
            </span>
          </Field>
          <Field label={t("labelLiving")}>
            <span className="text-sm text-ink/80">
              {w.livingArrangement
                ? LIVING_ARRANGEMENT_LABEL_VI[
                    w.livingArrangement as LivingArrangement
                  ]
                : "—"}
            </span>
          </Field>
          <Field label={t("labelMotorbike")}>
            <span className="text-sm text-ink/80">
              {w.hasMotorbike ? t("yes") : t("no")}
            </span>
          </Field>
          <Field label={t("labelTattoo")}>
            <span className="text-sm text-ink/80">
              {w.hasVisibleTattoo ? t("yes") : t("no")}
            </span>
          </Field>
        </FieldList>
      </Section>

      <Section title={t("sectionPhotos")}>
        {w.fullBodyImageUrls.length === 0 ? (
          <p className="text-sm text-ash">{t("noPhotos")}</p>
        ) : (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {w.fullBodyImageUrls.map((src) => (
              <img
                key={src}
                src={src}
                alt=""
                className="h-44 w-full rounded-lg object-cover"
              />
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <h4 className="mb-2 text-xs font-extrabold uppercase tracking-wide text-ink/60">
        {title}
      </h4>
      {children}
    </section>
  );
}

function FieldList({ children }: { children: React.ReactNode }) {
  return <div className="grid gap-3 sm:grid-cols-2">{children}</div>;
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-ash">
        {label}
      </p>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function ChipList({ items }: { items: string[] }) {
  if (items.length === 0) return <span className="text-sm text-ash">—</span>;
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((label) => (
        <span
          key={label}
          className="rounded-full bg-mist px-2.5 py-0.5 text-xs font-semibold text-ink"
        >
          {label}
        </span>
      ))}
    </div>
  );
}

// ── Experience: the primary hiring signal ─────────────────────────────
// F&B employers hire on concrete experience above everything else, so the
// experience block leads the body: a dark "snapshot" bar (total time +
// roles) on top, then a vertical timeline where each entry shows the
// worker-written description in a lime callout.

type WorkerView = ProfileData["worker"];
type ExperienceEntry = WorkerView["experiences"][number];

function ExperienceSection({
  w,
  t,
}: {
  w: WorkerView;
  t: (key: string, values?: Record<string, string | number>) => string;
}) {
  const hasExp = w.experiences.length > 0;
  return (
    <section>
      {hasExp ? (
        <div className="mb-4">
          <ExperienceSnapshot w={w} t={t} />
        </div>
      ) : null}
      <div className="mb-3 flex items-center gap-2">
        <h4 className="text-xs font-extrabold uppercase tracking-wide text-ink">
          {t("sectionExperience")}
        </h4>
        {hasExp ? (
          <span className="rounded-md bg-lime-light px-1.5 py-0.5 text-[10px] font-extrabold text-lime-dark">
            {t("experiencePriority")}
          </span>
        ) : null}
        <span className="h-px flex-1 bg-smoke" />
      </div>
      {!hasExp ? (
        <p className="text-sm text-ash">{t("noExperiences")}</p>
      ) : (
        <ol className="relative ml-1 space-y-4 border-l-2 border-smoke pl-5">
          {w.experiences.map((e) => (
            <ExperienceItem key={e.id} e={e} t={t} />
          ))}
        </ol>
      )}
    </section>
  );
}

function ExperienceItem({
  e,
  t,
}: {
  e: ExperienceEntry;
  t: (key: string, values?: Record<string, string | number>) => string;
}) {
  const position =
    WORKER_POSITION_LABEL_VI[e.position as WorkerPosition] ?? e.position;
  const duration = formatExpPeriod(e);
  const place = e.branch
    ? `${e.branch.orgName} · ${e.branch.alias}`
    : e.placeName?.trim() || null;
  const desc = e.description?.trim();
  return (
    <li className="relative">
      <span
        className={`absolute -left-[27px] top-1 h-3.5 w-3.5 rounded-full border-[3px] ${
          e.current ? "border-lime-dark bg-lime" : "border-lime-dark bg-paper"
        }`}
      />
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-extrabold text-ink">{position}</span>
        {e.current ? (
          <span className="rounded-md bg-lime px-1.5 py-0.5 text-[10px] font-extrabold text-ink">
            {t("experienceCurrent")}
          </span>
        ) : null}
        <span className="rounded-md bg-lime-light px-1.5 py-0.5 text-[11px] font-bold text-lime-dark">
          {duration}
        </span>
      </div>
      {place ? (
        <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[13px] text-ink/80">
          <span>{place}</span>
          {e.branch ? (
            e.branch.kycVerified ? (
              <span className="font-bold text-lime-dark">
                ✓ {t("experienceVerified")}
              </span>
            ) : null
          ) : (
            <span className="text-[11px] text-ash">
              ({t("experienceFreetext")})
            </span>
          )}
        </div>
      ) : null}
      {desc ? (
        <p className="mt-2 whitespace-pre-line rounded-r-lg border-l-[3px] border-lime bg-mist px-3 py-2 text-[13px] leading-relaxed text-ink/80">
          {desc}
        </p>
      ) : (
        <p className="mt-2 text-[12px] italic text-ash">
          {t("experienceNoDescription")}
        </p>
      )}
    </li>
  );
}

function ExperienceSnapshot({
  w,
  t,
}: {
  w: WorkerView;
  t: (key: string, values?: Record<string, string | number>) => string;
}) {
  const totalMonths = w.experiences.reduce(
    (sum, e) => sum + experienceMonths(e),
    0,
  );
  const roles: string[] = [];
  const seen = new Set<string>();
  for (const e of w.experiences) {
    const label =
      WORKER_POSITION_LABEL_VI[e.position as WorkerPosition] ?? e.position;
    if (!seen.has(label)) {
      seen.add(label);
      roles.push(label);
    }
  }
  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-3 rounded-2xl bg-ink p-4 text-paper">
      <div>
        <div className="text-2xl font-extrabold leading-none text-lime">
          {formatTotalExperience(totalMonths, t)}
        </div>
        <div className="mt-1 text-[10px] font-semibold uppercase tracking-wide text-paper/55">
          {t("experienceTotalLabel")}
        </div>
      </div>
      {roles.length > 0 ? (
        <>
          <div className="h-9 w-px bg-paper/20" />
          <div>
            <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-paper/55">
              {t("experienceRolesLabel")}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {roles.map((r) => (
                <span
                  key={r}
                  className="rounded-full bg-paper/15 px-2.5 py-1 text-xs font-bold"
                >
                  {r}
                </span>
              ))}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}

// Inclusive month span of one entry; an ongoing role counts up to today.
// A rough sum across entries is fine for a snapshot — overlapping part-time
// stints are rare in this segment and the number is a signal, not payroll.
function experienceMonths(e: {
  startMonth: number;
  startYear: number;
  endMonth: number | null;
  endYear: number | null;
  current: boolean;
}): number {
  const start = e.startYear * 12 + (e.startMonth - 1);
  let end: number;
  if (e.current || e.endMonth == null || e.endYear == null) {
    const now = new Date();
    end = now.getFullYear() * 12 + now.getMonth();
  } else {
    end = e.endYear * 12 + (e.endMonth - 1);
  }
  return Math.max(0, end - start) + 1;
}

function formatTotalExperience(
  months: number,
  t: (key: string, values?: Record<string, string | number>) => string,
): string {
  const y = Math.floor(months / 12);
  const m = months % 12;
  if (y > 0) return t("experienceYears", { years: y, months: m });
  return t("experienceMonthsOnly", { months: m });
}

function formatExpPeriod(e: {
  startMonth: number;
  startYear: number;
  endMonth: number | null;
  endYear: number | null;
  current: boolean;
}): string {
  const start = `${String(e.startMonth).padStart(2, "0")}/${e.startYear}`;
  if (e.current || e.endMonth == null || e.endYear == null) {
    return `${start} – Hiện tại`;
  }
  return `${start} – ${String(e.endMonth).padStart(2, "0")}/${e.endYear}`;
}

function formatSalary(
  min: string | number | bigint | null,
  max: string | number | bigint | null,
  type: string | null,
): string {
  const toNum = (v: string | number | bigint | null) => {
    if (v == null) return null;
    const n = typeof v === "bigint" ? Number(v) : Number(v);
    return Number.isFinite(n) ? n : null;
  };
  const lo = toNum(min);
  const hi = toNum(max);
  if (lo == null && hi == null) return "—";
  const suffix = type
    ? ` / ${SALARY_TYPE_LABEL_VI[type as SalaryType] ?? type}`
    : "";
  if (lo != null && hi != null && lo !== hi) {
    return `${formatVnd(lo)} – ${formatVnd(hi)}${suffix}`;
  }
  return `${formatVnd(lo ?? hi ?? 0)}${suffix}`;
}
